/**
 * Static, compile-time feature flags — the MVP "kill switch" layer.
 *
 * This is deliberately the *simplest possible* gating mechanism: a plain
 * object of booleans, evaluated at build time. It is **orthogonal** to the
 * three existing dynamic gating layers (see `src/features/types.ts`):
 *
 *   • `feature`    (per-org `enabled_features`) — "did this customer buy it?"
 *   • `Visibility` (`nav_overrides`)            — "how prominent is it?"
 *   • `Scope`      (`setting_type`)             — "praxis or klinik?"
 *   • `FEATURE_FLAGS` (this file)               — "is it part of the MVP at all?"
 *
 * Purpose: collapse the product from ~30 modules down to the three-screen
 * MVP ("open app → dictate notes → finished GNCP doc as PDF") **without
 * deleting a single line of code**. Everything that is `false` here is
 * hidden from navigation and its routes/tabs degrade to a graceful
 * "nicht verfügbar" placeholder instead of mounting. Flip a flag back to
 * `true` and the whole module returns, untouched.
 *
 * Because these are constants, the bundler tree-shakes disabled branches
 * in production. Re-enabling a feature is a one-line change here.
 */

export const FEATURE_FLAGS = {
  // ─── MVP Core — ACTIVE ─────────────────────────────────────────────────
  dashboard_simple: true,
  patients_core: true,
  gncp_documentation: true,
  voice_to_gncp: true,
  pdf_export: true,
  calendar_basic: true,

  // ─── Phase 2 — HIDDEN ──────────────────────────────────────────────────
  screening_tools: false,       // Public MVP: no automated screening/clinical pre-decision
  iddsi_assessment: false,
  lab_values: false,
  clinical_consults: true,      // Sold surface remains technically reachable; public MVP blocks it via enabled-feature boundary
  station_overview: true,       // Sold surface remains technically reachable; public MVP blocks it via enabled-feature boundary
  nursing_quick_doc: false,     // Pflege-Schnelldoku
  // ACTIVE: gate sold/built surfaces, so they must stay reachable. Enabled
  // to satisfy the "sold ⇒ reachable" invariant (Phase 0 — Governance &
  // Flag-Hygiene): the underlying features ship in customer plans
  // (FEATURE_PLANS), so the static kill switch in front of them cannot be
  // false. Enforced by src/test/nav-feature-flag-invariant.test.ts.
  diet_orders: true,            // Diätverordnungen, Küchen-Export, Ernährungspläne (sold: ernaehrungsplaene, klinik_kuechen_export, ernaehrungsplan_patient)
  nutriscreen: true,            // Patienten-Screening-App (sold: klinik_nutriscreen, FEATURE_PLANS.klinik)
  patient_portal: true,         // PWA, Ernährungstagebuch, Fragebogen-Engine
  telemedizin: true,            // Video consultations for the praxis MVP
  booking_page: false,          // Öffentliche Buchungsseite
  intake_page: false,           // Anamnese-Formular
  billing_module: true,         // GKV/PKV/Selbstzahler Abrechnung
  hl7_fhir: false,              // HL7 Inbound, FHIR ISiK, Konnektoren
  nutriknows_rag: false,        // Wissensmodul
  magic_importer: false,        // KI-Datenimport
  // ACTIVE: the per-org feature gate ("Funktion deaktiviert — ein
  // Administrator kann sie in den Einstellungen aktivieren") is only
  // truthful if the admin actually has a reachable place to do that.
  // These two flags gate the *management* surfaces:
  //   • multi_org_admin → "Organisationen" (Kundenverwaltung): the
  //     superadmin tool to pick any org and set its paket + enabled
  //     features per account, plus Support-Modus.
  //   • advanced_settings → Settings-Tab "Funktionen" (Workspace
  //     anpassen): org-admin self-service add-on toggles + paket switch.
  // With both false there was NO reachable UI to grant features, so every
  // account stayed stuck on its seeded basis set. Enabled so the superadmin
  // can administer which functions each (test) account receives.
  multi_org_admin: true,        // Plattform-Admin, Org-Verwaltung, Praxis-/Klinik-Admin, Therapeuten-Team
  advanced_settings: true,      // Nomenklatur, Integrationen, Portal-Module, E-Mail, Datenschutz-Tools

  // ─── Phase 2 — HIDDEN (additional modules present in this codebase) ─────
  // The list above is the canonical product taxonomy; these cover the
  // remaining standalone screens that exist in the route registry so that
  // *every* non-core surface has an explicit, reversible kill switch.
  reporting_analytics: true,          // Auswertungen / KPI-Statistiken
  messaging: true,                    // Therapeut↔Patient Nachrichten
  // ACTIVE: sold in FEATURE_PLANS.praxis, so they must stay reachable.
  // Enabled to satisfy the "sold ⇒ reachable" invariant (Phase 0 —
  // Governance & Flag-Hygiene); see nav-feature-flag-invariant.test.ts.
  waitlist: true,                     // Warteliste (sold: warteliste, FEATURE_PLANS.praxis)
  group_therapy: true,                // Gruppentherapie (sold: gruppen, FEATURE_PLANS.praxis)
  programs: false,                    // Programme
  session_recording_standalone: true, // Voice-Doku als eigener Praxis-Einstieg
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

export const isEnabled = (flag: FeatureFlag): boolean => FEATURE_FLAGS[flag];
